import React from 'react';

const whyChooseData = [
  { 
    icon: 'fa-user-tie', 
    title: 'Experienced Compliance Professionals',
    desc: 'Our team consists of seasoned Chartered Accountants, lawyers, and company secretaries with extensive legal and business expertise.'
  },
  { 
    icon: 'fa-file-signature', 
    title: 'Fast Documentation Process',
    desc: 'We leverage technology and streamlined workflows to complete drafting, filing, and registration tasks in record time.'
  },
  { 
    icon: 'fa-tags', 
    title: 'Affordable Pricing',
    desc: 'No hidden charges. We offer completely transparent and competitive pricing models tailored for startups and small businesses.'
  },
  { 
    icon: 'fa-headset', 
    title: 'Dedicated Client Support',
    desc: 'A dedicated relationship manager is assigned to guide you step-by-step and answer all queries throughout the compliance process.'
  },
  { 
    icon: 'fa-globe', 
    title: 'PAN India Service Network',
    desc: 'Whether you are in Mumbai, Delhi, Bangalore, or a remote town, our digital services cover all states and territories across India.'
  },
  { 
    icon: 'fa-circle-check', 
    title: 'End-to-End Compliance Assistance',
    desc: 'From initial registration to monthly returns and annual audits, we handle everything under one unified roof.'
  },
  { 
    icon: 'fa-shield-halved', 
    title: 'Secure and Confidential Process',
    desc: 'Your business ideas and personal details are protected by enterprise-grade security protocol and strict NDAs.'
  }
];

const Features = () => {
  return (
    <section id="why-us" className="section-padding bg-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .whu-section {
          font-family: 'Inter', sans-serif;
        }

        .whu-banner {
          background: linear-gradient(135deg, #040f1f 0%, #061830 60%, #CFA047 100%);
          border-radius: 28px;
          padding: 60px 48px;
          margin-bottom: 56px;
          position: relative;
          overflow: hidden;
          text-align: center;
          box-shadow: 0 20px 40px rgba(6, 24, 48, 0.15);
        }

        .whu-banner::before {
          content: '';
          position: absolute;
          top: -80px; right: -80px;
          width: 300px; height: 300px;
          background: radial-gradient(circle, rgba(255,255,255,0.08), transparent 70%);
          pointer-events: none;
        }

        .whu-banner::after {
          content: '';
          position: absolute;
          bottom: -60px; left: -60px;
          width: 240px; height: 240px;
          background: radial-gradient(circle, rgba(255,255,255,0.06), transparent 70%);
          pointer-events: none;
        }

        .whu-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.2);
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          padding: 6px 16px;
          border-radius: 20px;
          margin-bottom: 20px;
        }

        .whu-eyebrow-dot {
          width: 6px; height: 6px;
          background: #CFA047;
          border-radius: 50%;
          box-shadow: 0 0 6px rgba(207, 160, 71, 0.5);
        }

        .whu-heading {
          font-size: 42px;
          font-weight: 800;
          color: #fff;
          letter-spacing: -1.5px;
          line-height: 1.15;
          margin: 0 0 16px;
          position: relative;
          z-index: 1;
        }

        .whu-subtext {
          font-size: 16px;
          color: rgba(255,255,255,0.75);
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.8;
          position: relative;
          z-index: 1;
        }

        /* ── Feature grid ── */
        .whu-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        @media (max-width: 991px) { .whu-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 767px) { 
          .whu-grid { grid-template-columns: 1fr; }
          .whu-banner { padding: 40px 24px; }
          .whu-heading { font-size: 32px; }
        }

        /* ── Feature card ── */
        .whu-card {
          background: #fff;
          border: 1.5px solid #e8edf3;
          border-radius: 20px;
          padding: 30px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
          cursor: default;
          text-align: left;
        }

        .whu-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 16px 36px rgba(6, 24, 48, 0.08);
          border-color: #061830;
        }

        .whu-icon {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          background: #ffffff;
          border: 1.5px solid rgba(207, 160, 71, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          color: #061830;
          margin-bottom: 22px;
          transition: background 0.22s, color 0.22s, border-color 0.22s;
        }

        .whu-card:hover .whu-icon {
          background: #061830;
          color: #fff;
          border-color: #061830;
        }

        .whu-card-title {
          font-size: 17px;
          font-weight: 800;
          color: #0f172a;
          line-height: 1.4;
          margin-bottom: 10px;
        }

        .whu-card-desc {
          font-size: 13.5px;
          color: #475569;
          line-height: 1.7;
          margin: 0;
        }
      `}</style>

      <div className="container whu-section">
        {/* Banner */}
        <div className="whu-banner">
          <div className="whu-eyebrow">
            <span className="whu-eyebrow-dot"></span>
            Why Choose Legal Infos
          </div>
          <h2 className="whu-heading">Why Businesses Choose Legal Infos</h2>
          <p className="whu-subtext">
            We are dedicated to helping entrepreneurs incorporate, operate, and grow their businesses across India with confidence and speed.
          </p>
        </div>

        {/* Grid */}
        <div className="whu-grid">
          {whyChooseData.map((item, i) => (
            <div key={i} className="whu-card">
              <div className="whu-icon">
                <i className={`fa-solid ${item.icon}`}></i>
              </div>
              <h5 className="whu-card-title">{item.title}</h5>
              <p className="whu-card-desc">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
